import type { Density, Grid, ViewKey } from './views'
import { GRIDS, VIEWS, defaultView, opens } from './views'

/**
 * The address bar as state.
 *
 * The front page's continuations name a window — the rest of this week, the rest of the
 * month — and without somewhere to put that, every one of them landed on whatever month the
 * calendar happened to open on. A link that resolves and lands on the wrong thing is worse
 * than one that fails: nothing tells you it went wrong, so it is the original complaint
 * surviving its own fix.
 *
 * Written back with `replaceState`, so the back button still means "the page before this
 * one" rather than "the last tab I pressed".
 */
/** How much of the calendar a list is about. `null` is all of it, from `on` onwards. */
export type Only = 'day' | 'week' | 'month'

export interface CalendarAddress {
  view: ViewKey
  grid: Grid
  /** The night the reader asked for, `YYYY-MM-DD`. The month it falls in is the month to
   *  open — a night rather than a month because "the rest of this week" starts on a
   *  Thursday, not on the 1st. */
  on: string | null
  /**
   * The window a list is confined to.
   *
   * A list opened from the tab bar is a way of browsing everything, and it pages forward
   * for ever. A list opened by pressing "+4 more" on a Thursday is an answer to a question
   * about that Thursday, and scrolling it into September is not what the press meant — it
   * is the same control giving two different answers depending on how you arrived, which is
   * the thing that makes a calendar feel like it is guessing.
   */
  only: Only | null
  /**
   * How much of each gig the list shows.
   *
   * In the address like everything else here. It was the one calendar control held in
   * component state, so it was lost on reload and could not be sent to anybody — against
   * this file's own principle that a view you cannot send is not much of a calendar.
   *
   * Only meaningful on the list, and only off a phone: `densityFor` forces compact at that
   * width, because two cards abreast at 320px is a thumbnail and half a name.
   */
  density: Density
}

const NIGHT = /^\d{4}-\d{2}-\d{2}$/

export function readAddress(
  search: string,
  { me, phone }: { me: { isEditor: boolean } | null; phone: boolean },
): CalendarAddress {
  const q = new URLSearchParams(search)
  const asked = q.get('view')
  let view = defaultView(me)
  if (asked && VIEWS.some(v => v.key === asked)) view = asked as ViewKey
  // A view that is not this reader's to open — one about the reader, with no reader.
  // `opens` is the tab row's own rule; the count half of it is the calendar's to apply,
  // since an address has no counts to give.
  if (!opens(view, me)) view = 'picked'

  const grid = q.get('grid')
  const on = q.get('on')
  const only = q.get('only')
  return {
    view,
    grid: grid && GRIDS.has(grid as Grid) ? (grid as Grid) : phone ? 'list' : 'month',
    on: on && NIGHT.test(on) ? on : null,
    // Meaningless without a night to be a window around, and meaningless off the list.
    only:
      only === 'day' || only === 'week' || only === 'month'
        ? on && NIGHT.test(on)
          ? (only as Only)
          : null
        : null,
    density: q.get('density') === 'compact' ? 'compact' : 'full',
  }
}

/**
 * Only what has been chosen, so a first visit keeps a clean address.
 *
 * `on` carries the night, never the first of its month. `?on=2026-08-20` opened the week of
 * the 17th correctly and was then overwritten with `2026-08-01`, so reloading that settled
 * address landed on the week of 27 July — the page manufacturing exactly the link the
 * comment above calls worse than a dead one, one second after honouring the good one.
 */
export function writeAddress(
  state: CalendarAddress,
  { me, phone }: { me: { isEditor: boolean } | null; phone: boolean },
): string {
  const q = new URLSearchParams()
  if (state.view !== defaultView(me)) q.set('view', state.view)
  if (state.grid !== (phone ? 'list' : 'month')) q.set('grid', state.grid)
  if (state.on) q.set('on', state.on)
  if (state.only && state.grid === 'list') q.set('only', state.only)
  // Not on a phone, where the choice is not the reader's to make and writing it would put a
  // parameter in the address that the same address ignores.
  if (state.density === 'compact' && state.grid === 'list' && !phone) q.set('density', 'compact')
  const query = q.toString()
  return query ? `?${query}` : ''
}

/** The Monday of the week a night falls in. Midday UTC, so no zone can shift the date. */
export function mondayOf(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7))
  return d.toISOString().slice(0, 10)
}

/** Whether the week a night falls in starts in one month and ends in the next. */
export function weekSpansMonths(iso: string): boolean {
  const monday = mondayOf(iso)
  const end = new Date(`${monday}T12:00:00Z`)
  end.setUTCDate(end.getUTCDate() + 6)
  return monday.slice(5, 7) !== end.toISOString().slice(5, 7)
}

/**
 * A week, said the way a reader says it: `17 – 23 August 2026`, with the month named twice
 * only when the week crosses one.
 *
 * Takes any day of the week and finds the Monday itself, which is the point. The toolbar
 * built its label from the anchor date while the grid drew `mondayOf` it, so the two agreed
 * only when the anchor was already a Monday — a link from the front page looked right and
 * the WEEK button did not. Doing it here means the caller cannot make that mistake again;
 * doing it at the call site is what the mistake *was*.
 *
 * The month names are passed in rather than held here: the calendar page already owns that
 * list, and two copies is two places for August to be spelt differently.
 */
export function weekLabel(anchor: string, months: readonly string[]): string {
  const monday = mondayOf(anchor)
  const end = new Date(`${monday}T12:00:00Z`)
  end.setUTCDate(end.getUTCDate() + 6)
  const sunday = end.toISOString().slice(0, 10)
  const day = (iso: string) => Number(iso.slice(8))
  const mon = (iso: string) => months[Number(iso.slice(5, 7)) - 1]
  return mon(monday) === mon(sunday)
    ? `${day(monday)} – ${day(sunday)} ${mon(sunday)} ${sunday.slice(0, 4)}`
    : `${day(monday)} ${mon(monday)} – ${day(sunday)} ${mon(sunday)} ${sunday.slice(0, 4)}`
}

/** One density on a phone: two cards abreast at 320px is a thumbnail and half a name. */
export function densityFor(phone: boolean, chosen: Density): Density {
  return phone ? 'compact' : chosen
}
